import {
  Badge,
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  Stack,
  Typography,
} from '@mui/material';
import { roleLabel } from '../../utils/labels';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { APP_VERSION } from '../../config/app';
import { BrandMark } from './BrandMark';
import { getSidebarNav } from './navConfig';
import { canComposeAlerts, canManageSources, canManageUsers, canViewAnalytics } from '../../utils/roles';
import { alpha, tokens } from '../../theme/tokens';

function isItemActive(pathname, item) {
  if (item.to === '/') {
    return pathname === '/';
  }
  if (item.end) {
    if (item.to === '/admin') {
      return pathname === '/admin' || pathname === '/admin/';
    }
    return pathname === item.to;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function SideNav({ onNavigate }) {
  const { user, isStaff, logout, unreadAlertCount } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sections = getSidebarNav({
    isStaff,
    canComposeAlerts: canComposeAlerts(user?.role),
    canManageSources: canManageSources(user?.role),
    canManageUsers: canManageUsers(user?.role),
    canViewAnalytics: canViewAnalytics(user?.role),
  });

  return (
    <Box
      sx={{
        flex: 1,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'transparent',
        color: tokens.onShell,
        colorScheme: 'dark',
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.75, pb: 2.25 }}>
        <BrandMark always />
        <Typography
          variant="body2"
          color="inherit"
          sx={{
            mt: 1,
            lineHeight: 1.45,
            color: `${tokens.onShellMuted} !important`,
            WebkitTextFillColor: tokens.onShellMuted,
          }}
        >
          {user ? `Signed in as ${user.name}` : 'Browse as guest'}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: alpha('#FFFFFF', 0.16) }} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          py: 2.25,
          px: 1,
        }}
      >
        {sections.map((section, index) => (
          <Box
            key={section.id}
            sx={{ mb: index === sections.length - 1 ? 0 : 2.75 }}
          >
            <Typography
              variant="caption"
              color="inherit"
              sx={{
                px: 1.75,
                mb: 1,
                display: 'block',
                fontWeight: 700,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: `${tokens.onShellMuted} !important`,
                WebkitTextFillColor: tokens.onShellMuted,
              }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isItemActive(location.pathname, item);
                return (
                  <ListItemButton
                    key={item.to}
                    component={NavLink}
                    to={item.to}
                    replace
                    end={Boolean(item.end)}
                    onClick={onNavigate}
                    selected={active}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      mx: 0.75,
                      mb: 0.5,
                      px: 1.5,
                      py: 1.25,
                      minHeight: 48,
                      borderRadius: '12px',
                      alignItems: 'center',
          color: tokens.onShell,
          transition:
            'background-color 220ms ease, transform 220ms ease',
          '&, &:link, &:visited, &:hover, &:active': {
            color: tokens.onShell,
            textDecoration: 'none',
          },
          '&.Mui-selected': {
            bgcolor: alpha('#FFFFFF', 0.12),
            color: tokens.onShell,
                        borderLeft: '3px solid',
                        borderColor: tokens.brand,
                        pl: 'calc(12px - 3px)',
                        '&:hover': { bgcolor: alpha('#FFFFFF', 0.16) },
                        '& .MuiListItemIcon-root': { color: tokens.brand },
                      },
                      '&:hover': {
                        bgcolor: alpha('#FFFFFF', 0.1),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: active ? tokens.brand : tokens.onShellMuted,
                      }}
                    >
                      {item.to === '/alerts' && unreadAlertCount > 0 ? (
                        <Badge
                          color="secondary"
                          badgeContent={unreadAlertCount}
                          max={99}
                        >
                          <Icon sx={{ fontSize: 24 }} />
                        </Badge>
                      ) : (
                        <Icon sx={{ fontSize: 24 }} />
                      )}
                    </ListItemIcon>
                    {/* Do not use minWidth:0 + overflow:hidden — iOS Safari
                        collapses the flex label to 0 width on first drawer open. */}
                    <Typography
                      component="span"
                      color="inherit"
                      sx={{
                        flex: '1 1 auto',
                        minWidth: '4.5rem',
                        fontWeight: active ? 700 : 600,
                        fontSize: '1rem',
                        lineHeight: 1.3,
                        color: `${tokens.onShell} !important`,
                        WebkitTextFillColor: `${tokens.onShell} !important`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 2.5,
          pt: 1.5,
          pb: 'calc(16px + var(--safe-bottom))',
          borderTop: `1px solid ${alpha('#FFFFFF', 0.16)}`,
          bgcolor: 'transparent',
        }}
      >
        {user ? (
          <Stack spacing={1}>
            <Box
              sx={{
                '@media (max-height: 720px)': { display: 'none' },
              }}
            >
              <Typography
                variant="body2"
                color="inherit"
                sx={{
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                  color: tokens.onShellMuted,
                }}
              >
                {user.email}
              </Typography>
              <Typography
                variant="caption"
                color="inherit"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  fontWeight: 600,
                  color: tokens.onShellMuted,
                }}
              >
                {roleLabel(user.role)}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                bgcolor: 'transparent !important',
                color: `${tokens.onShell} !important`,
                borderColor: alpha('#FFFFFF', 0.28),
                '&:hover': {
                  bgcolor: `${alpha('#FFFFFF', 0.1)} !important`,
                  borderColor: alpha('#FFFFFF', 0.45),
                  color: `${tokens.onShell} !important`,
                },
                '&.Mui-disabled': {
                  color: alpha(tokens.onShell, 0.4),
                  borderColor: alpha('#FFFFFF', 0.12),
                },
              }}
              onClick={async () => {
                await logout();
                onNavigate?.();
                navigate('/login', { replace: true });
              }}
            >
              Sign out
            </Button>
            <Typography
              variant="caption"
              color="inherit"
              sx={{
                display: 'block',
                textAlign: 'center',
                bgcolor: 'transparent',
                color: `${tokens.onShellMuted} !important`,
                WebkitTextFillColor: `${tokens.onShellMuted} !important`,
                fontSize: '0.8125rem',
              }}
            >
              Version - {APP_VERSION}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Button
              component={NavLink}
              to="/login"
              state={{ from: location.pathname }}
              variant="contained"
              color="secondary"
              fullWidth
              size="large"
              onClick={onNavigate}
              sx={{
                bgcolor: tokens.brandFill,
                color: '#FFFFFF',
                WebkitTextFillColor: '#FFFFFF',
                '&:hover': {
                  bgcolor: tokens.brandInk,
                  color: '#FFFFFF',
                  WebkitTextFillColor: '#FFFFFF',
                },
              }}
            >
              Sign in
            </Button>
            <Button
              component={NavLink}
              to="/signup"
              state={{ from: location.pathname }}
              variant="outlined"
              fullWidth
              size="large"
              onClick={onNavigate}
              sx={{
                color: tokens.onShell,
                borderColor: alpha('#FFFFFF', 0.28),
                '&:hover': {
                  borderColor: alpha('#FFFFFF', 0.45),
                  bgcolor: alpha('#FFFFFF', 0.08),
                },
              }}
            >
              Create account
            </Button>
            <Typography
              variant="caption"
              color="inherit"
              sx={{
                display: 'block',
                textAlign: 'center',
                bgcolor: 'transparent',
                color: `${tokens.onShellMuted} !important`,
                WebkitTextFillColor: `${tokens.onShellMuted} !important`,
                fontSize: '0.8125rem',
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
